import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { IMaskInput } from "react-imask";
import api from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { openRegistrationReceipt } from "../utils/printReceipt";
import "./McmvForm.css";

// Helper para formatar erros de validação vindos do backend (Laravel)
const formatBackendErrors = (err: any): string | null => {
  const errors = err?.response?.data?.errors;
  if (errors && typeof errors === "object") {
    try {
      const lines = Object.entries(errors).map(([field, msgs]) => {
        const joined = Array.isArray(msgs) ? msgs.join(", ") : String(msgs);
        return `${field}: ${joined}`;
      });
      return lines.join("\n");
    } catch (_) {
      // Falha ao formatar, segue abaixo
    }
  }
  // Fallback para mensagem genérica do backend ou do Axios
  return err?.response?.data?.message || err?.message || null;
};

// Converte string em formato BRL (ex.: "1.000,00") para número
const parseBRLCurrency = (value: string | undefined | null): number | null => {
  if (value == null) return null;
  const trimmed = String(value).replace(/\s|R\$/g, "").trim();
  if (!trimmed) return null;
  const normalized = trimmed.replace(/\./g, "").replace(/,/g, ".");
  const num = parseFloat(normalized);
  return Number.isFinite(num) ? num : null;
};

// Formata número ou string numérica para BRL com 2 casas (ex.: 400 -> "400,00", 3500 -> "3.500,00")
const formatBRLCurrency = (value: number | string | undefined | null): string => {
  if (value === undefined || value === null || value === "") return "";
  let num: number | null = null;
  if (typeof value === "number") {
    num = value;
  } else {
    // normaliza possíveis formatos de entrada: "400", "400.00", "3.500", "3.500,00"
    const raw = String(value).replace(/\s|R\$/g, "").trim();
    if (!raw) return "";
    const hasComma = raw.includes(",");
    const hasDot = raw.includes(".");
    let normalized = raw;
    if (hasComma) {
      // quando há vírgula, pontos são milhares e vírgula é decimal
      normalized = normalized.replace(/\./g, "").replace(/,/g, ".");
    } else if (hasDot) {
      // quando só há ponto, decide se é milhares ou decimal pelo tamanho do sufixo
      const parts = normalized.split(".");
      const last = parts[parts.length - 1];
      if (last.length === 3) {
        // ex.: "3.500" -> milhares, remover pontos
        normalized = normalized.replace(/\./g, "");
      }
      // caso "400.00" (duas casas) mantém ponto como decimal
    }
    const parsed = parseFloat(normalized);
    num = Number.isFinite(parsed) ? parsed : null;
  }
  if (num === null) return "";
  return new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
};

type FormData = {
  // Dados pessoais
  fullName: string;
  cpf: string;
  rg: string;
  birthDate: string;
  sexo?: 'M' | 'F' | '';
  maritalStatus: "solteiro" | "casado" | "uniao_estavel" | "divorciado" | "viuvo" | "";
  phone: string;
  email?: string;
  isResponsible: "yes" | "no";
  responsibleName?: string;

  // Endereço
  address: string;
  city: string;
  state: string;
  cep: string;

  // Família
  familySize: string;
  hasChildren: "yes" | "no";
  hasElderly: "yes" | "no";
  isSingleMother: "yes" | "no";
  hasPCD: "yes" | "no";

  // Renda
  incomeIndividual: string;
  incomeFamily: string;

  // Critérios
  residenceYears: string;
  cadunico: string;
  hasImmovable: "yes" | "no";
  previousHousingProgram: "yes" | "no";
  selfIsElderly: "yes" | "no";
  selfIsPCD: "yes" | "no";

  // Elegibilidade I–VI
  habitacaoPrecaria?: "yes" | "no";
  paredeNaoAlvenariaOuMadeira?: "yes" | "no";
  domicilioImprovisado?: "yes" | "no";
  coabitacao?: "yes" | "no";
  autodeclaracaoCoabitacao?: string;
  domicilioAlugado?: "yes" | "no";
  totalResidentes?: string;
  numDormitorios?: string;
  aluguelValorMensal?: string;
  aluguelSocialProvisorio?: "yes" | "no";
  situacaoRua?: "rua" | "trajetoria_rua" | "nao" | "";

  // Critérios b–l
  pessoaNegraFamiliaCadunico?: "yes" | "no";
  pessoaComDeficienciaFamilia?: "yes" | "no";
  idosoNaFamilia?: "yes" | "no";
  criancaOuAdolescenteFamilia?: "yes" | "no";
  cancerOuDoencaRaraDegenerativa?: "yes" | "no";
  mulherVitimaViolenciaDomestica?: "yes" | "no";
  indigenaQuilombola?: "indigena" | "quilombola" | "ambos" | "nao" | "";
  residenteAreaRisco?: "yes" | "no";
  tipoRisco?: "deslizamento" | "inundacao_brusca" | "processo_geologico_hidrologico" | "outros" | "nao" | "";
  contratoDistratadoOuRescindido?: "yes" | "no";
  coabitacaoRelatorioSocialSMASC?: "yes" | "no";
  situacaoOcupacional?: "desemprego" | "subemprego" | "mercado_informal" | "emprego_formal" | "autonomo" | "outros" | "";

  declaration: boolean;
};

const schema: yup.ObjectSchema<FormData> = yup.object({
  fullName: yup.string().required("Nome completo é obrigatório"),
  cpf: yup
    .string()
    .required("CPF é obrigatório")
    .test("cpf-length", "CPF deve ter 11 dígitos", (value) => {
      if (!value) return false;
      const clean = value.replace(/\D/g, "");
      return clean.length === 11;
    }),
  rg: yup.string().required("RG é obrigatório"),
  birthDate: yup.string().required("Data de nascimento é obrigatória"),
  sexo: yup.string().oneOf(['M', 'F', ''], 'Sexo inválido').optional(),
  maritalStatus: yup
    .mixed<FormData["maritalStatus"]>()
    .oneOf(["solteiro", "casado", "uniao_estavel", "divorciado", "viuvo", ""], "Estado civil inválido")
    .required("Estado civil é obrigatório"),
  phone: yup
    .string()
    .required("Telefone é obrigatório")
    .test("phone-length", "Telefone deve ter 10 ou 11 dígitos", (value) => {
      if (!value) return false;
      const clean = value.replace(/\D/g, "");
      return clean.length >= 10 && clean.length <= 11;
    }),
  email: yup
    .string()
    .transform((v) => (v === "" ? undefined : v))
    .email("Email inválido")
    .optional(),
  isResponsible: yup.mixed<FormData["isResponsible"]>().oneOf(["yes", "no"]).required(),
  responsibleName: yup.string().optional(),

  address: yup.string().required("Endereço é obrigatório"),
  city: yup.string().required("Cidade é obrigatória"),
  state: yup
    .string()
    .transform((v) => (v ? v.trim().toUpperCase() : v))
    .length(2, "Estado deve ter 2 letras (UF)")
    .required("Estado é obrigatório"),
  cep: yup
    .string()
    .required("CEP é obrigatório")
    .test("cep-length", "CEP deve ter 8 dígitos", (value) => {
      if (!value) return false;
      const clean = value.replace(/\D/g, "");
      return clean.length === 8;
    }),

  familySize: yup.string().required("Número de pessoas na família é obrigatório"),
  hasChildren: yup.mixed<FormData["hasChildren"]>().oneOf(["yes", "no"]).required(),
  hasElderly: yup.mixed<FormData["hasElderly"]>().oneOf(["yes", "no"]).required(),
  isSingleMother: yup.mixed<FormData["isSingleMother"]>().oneOf(["yes", "no"]).required(),
  hasPCD: yup.mixed<FormData["hasPCD"]>().oneOf(["yes", "no"]).required(),

  incomeIndividual: yup
    .string()
    .required("Renda individual é obrigatória")
    .test("currency-brl", "Informe um valor válido (ex.: 400,00)", (val) => parseBRLCurrency(val) !== null),
  incomeFamily: yup
    .string()
    .required("Renda familiar é obrigatória")
    .test("currency-brl", "Informe um valor válido (ex.: 1.000,00)", (val) => parseBRLCurrency(val) !== null),

  residenceYears: yup.string().required("Tempo de residência é obrigatório"),
  cadunico: yup.string().required("CadÚnico é obrigatório"),
  hasImmovable: yup.mixed<FormData["hasImmovable"]>().oneOf(["yes", "no"]).required(),
  previousHousingProgram: yup.mixed<FormData["previousHousingProgram"]>().oneOf(["yes", "no"]).required(),
  selfIsElderly: yup.mixed<FormData["selfIsElderly"]>().oneOf(["yes", "no"]).required(),
  selfIsPCD: yup.mixed<FormData["selfIsPCD"]>().oneOf(["yes", "no"]).required(),

  // Elegibilidade I–VI
  habitacaoPrecaria: yup.string().oneOf(['yes','no']).optional(),
  paredeNaoAlvenariaOuMadeira: yup.string().oneOf(['yes','no']).optional(),
  domicilioImprovisado: yup.string().oneOf(['yes','no']).optional(),
  coabitacao: yup.string().oneOf(['yes','no']).optional(),
  autodeclaracaoCoabitacao: yup.string().max(255).optional(),
  domicilioAlugado: yup.string().oneOf(['yes','no']).optional(),
  totalResidentes: yup
    .string()
    .optional()
    .test(
      'total-residentes-nao-negativo',
      'Total de residentes não pode ser negativo',
      (value) => !value || Number(value) >= 0
    ),
  numDormitorios: yup.string().optional(),
  aluguelValorMensal: yup.string().optional(),
  aluguelSocialProvisorio: yup.string().oneOf(['yes','no']).optional(),
  situacaoRua: yup.string().oneOf(['rua','trajetoria_rua','nao','']).optional(),

  // Critérios b–l
  pessoaNegraFamiliaCadunico: yup.string().oneOf(['yes','no']).optional(),
  pessoaComDeficienciaFamilia: yup.string().oneOf(['yes','no']).optional(),
  idosoNaFamilia: yup.string().oneOf(['yes','no']).optional(),
  criancaOuAdolescenteFamilia: yup.string().oneOf(['yes','no']).optional(),
  cancerOuDoencaRaraDegenerativa: yup.string().oneOf(['yes','no']).optional(),
  mulherVitimaViolenciaDomestica: yup.string().oneOf(['yes','no']).optional(),
  indigenaQuilombola: yup.string().oneOf(['indigena','quilombola','ambos','nao','']).optional(),
  residenteAreaRisco: yup.string().oneOf(['yes','no']).optional(),
  tipoRisco: yup.string().oneOf(['deslizamento','inundacao_brusca','processo_geologico_hidrologico','outros','nao','']).optional(),
  contratoDistratadoOuRescindido: yup.string().oneOf(['yes','no']).optional(),
  coabitacaoRelatorioSocialSMASC: yup.string().oneOf(['yes','no']).optional(),
  situacaoOcupacional: yup.string().oneOf(['desemprego','subemprego','mercado_informal','emprego_formal','autonomo','outros','']).optional(),

  declaration: yup
    .boolean()
    .required("Você deve aceitar a declaração")
    .oneOf([true], "Você deve aceitar a declaração"),
});

export default function McmvRegistrationForm({
  disablePrefill = false,
  disableLoadOwnRegistration = false,
  editId,
  onSuccess,
}: {
  disablePrefill?: boolean;
  disableLoadOwnRegistration?: boolean;
  editId?: number;
  onSuccess?: () => void;
}) {
  const [currentStep, setCurrentStep] = useState(1);
  const { user, setRegistrationData } = useAuth();
  const [submitMessage, setSubmitMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    reset,
    watch,
    trigger,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      fullName: "",
      cpf: "",
      rg: "",
      birthDate: "",
      maritalStatus: "",
      phone: "",
      email: "",
      isResponsible: "yes",
      responsibleName: "",
      address: "",
      city: "",
      state: "",
      cep: "",
      familySize: "",
      hasChildren: "no",
      hasElderly: "no",
      isSingleMother: "no",
      hasPCD: "no",
      incomeIndividual: "",
      incomeFamily: "",
      residenceYears: "",
      cadunico: "",
      hasImmovable: "no",
      previousHousingProgram: "no",
      selfIsElderly: "no",
      selfIsPCD: "no",
      // Elegibilidade I–VI
      habitacaoPrecaria: "no",
      paredeNaoAlvenariaOuMadeira: "no",
      domicilioImprovisado: "no",
      coabitacao: "no",
      autodeclaracaoCoabitacao: "",
      domicilioAlugado: "no",
      totalResidentes: "",
      numDormitorios: "",
      aluguelValorMensal: "",
      aluguelSocialProvisorio: "no",
      situacaoRua: "",
      // Critérios b–l
      pessoaNegraFamiliaCadunico: "no",
      pessoaComDeficienciaFamilia: "no",
      idosoNaFamilia: "no",
      criancaOuAdolescenteFamilia: "no",
      cancerOuDoencaRaraDegenerativa: "no",
      mulherVitimaViolenciaDomestica: "no",
      indigenaQuilombola: "",
      residenteAreaRisco: "no",
      tipoRisco: "",
      contratoDistratadoOuRescindido: "no",
      coabitacaoRelatorioSocialSMASC: "no",
      situacaoOcupacional: "",
      declaration: false,
    },
  });

  const searchCep = async (cep: string) => {
    if (cep.length === 8) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
        const data = await response.json();
        if (!data.erro) {
          setValue("address", data.logradouro || "");
          setValue("city", data.localidade || "");
          setValue("state", data.uf || "");
        }
      } catch (error) {
        console.error("Erro ao buscar CEP:", error);
      }
    }
  };

  const getFieldsForStep = (step: number): (keyof FormData)[] => {
    switch (step) {
      case 1:
        return [
          "fullName",
          "cpf",
          "rg",
          "birthDate",
          "sexo",
          "maritalStatus",
          "phone",
          "email",
          "isResponsible",
          "responsibleName",
        ];
      case 2:
        return ["cep", "address", "city", "state"];
      case 3:
        return ["familySize", "hasChildren", "hasElderly", "isSingleMother", "hasPCD"];
      case 4:
        return ["incomeIndividual", "incomeFamily"];
      case 5:
        return [
          "residenceYears",
          "cadunico",
          "hasImmovable",
          "previousHousingProgram",
          "selfIsElderly",
          "selfIsPCD",
          // Elegibilidade I–VI
          'habitacaoPrecaria',
          'paredeNaoAlvenariaOuMadeira',
          'domicilioImprovisado',
          'coabitacao',
          'autodeclaracaoCoabitacao',
          'domicilioAlugado',
          'totalResidentes',
          'numDormitorios',
          'aluguelValorMensal',
          'aluguelSocialProvisorio',
          'situacaoRua',
          // Critérios b–l
          'pessoaNegraFamiliaCadunico',
          'pessoaComDeficienciaFamilia',
          'idosoNaFamilia',
          'criancaOuAdolescenteFamilia',
          'cancerOuDoencaRaraDegenerativa',
          'mulherVitimaViolenciaDomestica',
          'indigenaQuilombola',
          'residenteAreaRisco',
          'tipoRisco',
          'contratoDistratadoOuRescindido',
          'coabitacaoRelatorioSocialSMASC',
          'situacaoOcupacional',
        ];
      case 6:
        return ["declaration"];
      default:
        return [];
    }
  };

  const nextStep = async () => {
    const fields = getFieldsForStep(currentStep);
    const ok = await trigger(fields);
    if (ok) setCurrentStep((s) => s + 1);
  };
  const prevStep = () => setCurrentStep((s) => s - 1);

  // Prefill: usuário logado
  useEffect(() => {
    if (user && !disablePrefill) {
      const birth = user.birth_date ? user.birth_date.split("T")[0] : "";
      setValue("fullName", user.name || "");
      setValue("cpf", user.cpf || "");
      setValue("birthDate", birth || "");
      setValue("email", user.email || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, disablePrefill]);

  // Prefill: edição por ID ou pré-inscrição própria
  useEffect(() => {
    const loadData = async () => {
      try {
        let data;
        if (editId) {
          const res = await api.get(`/mcmv/pre-registrations/${editId}`);
          data = res.data?.data || res.data;
        } else {
          if (disableLoadOwnRegistration) return;
          const res = await api.get("/mcmv/pre-registration");
          data = res.data?.data || res.data;
        }
        if (data) {
          const birth = data.data_nascimento ? String(data.data_nascimento).slice(0, 10) : "";
          reset({
            fullName: data.nome_completo || "",
            cpf: data.cpf || "",
            rg: data.rg || "",
            birthDate: birth,
            sexo: (data.sexo as FormData['sexo']) || '',
            maritalStatus: (data.estado_civil as FormData["maritalStatus"]) || "",
            phone: data.telefone || "",
            email: data.email || "",
            isResponsible: data.is_responsavel ? "yes" : "no",
            responsibleName: data.nome_responsavel || "",
            address: data.endereco || "",
            city: data.cidade || "",
            state: data.estado || "",
            cep: data.cep || "",
            familySize: String(data.tamanho_familia || ""),
            hasChildren: data.possui_filhos ? "yes" : "no",
            hasElderly: data.possui_idosos ? "yes" : "no",
            isSingleMother: data.mae_solo ? "yes" : "no",
            hasPCD: data.possui_pcd ? "yes" : "no",
            incomeIndividual: formatBRLCurrency(data.renda_individual),
            incomeFamily: formatBRLCurrency(data.renda_familiar),
            residenceYears: String(data.tempo_residencia_cidade ?? ""),
            cadunico: data.cadunico || "",
            hasImmovable: data.possui_imovel ? "yes" : "no",
            previousHousingProgram: data.participou_programa_moradia ? "yes" : "no",
            selfIsElderly: data.idoso ? "yes" : "no",
            selfIsPCD: data.pcd ? "yes" : "no",
            // Elegibilidade I–VI
            habitacaoPrecaria: data.habitacao_precaria ? 'yes' : 'no',
            paredeNaoAlvenariaOuMadeira: data.parede_nao_alvenaria_ou_madeira_aparelhada ? 'yes' : 'no',
            domicilioImprovisado: data.domicilio_particular_improvisado ? 'yes' : 'no',
            coabitacao: data.coabitacao ? 'yes' : 'no',
            autodeclaracaoCoabitacao: data.autodeclaracao_coabitacao || "",
            domicilioAlugado: data.domicilio_alugado ? 'yes' : 'no',
            totalResidentes: String(data.total_residentes ?? ''),
            numDormitorios: String(data.num_dormitorios ?? ''),
            aluguelValorMensal: formatBRLCurrency(data.aluguel_valor_mensal),
            aluguelSocialProvisorio: data.aluguel_social_provisorio ? 'yes' : 'no',
            situacaoRua: (data.situacao_rua as FormData['situacaoRua']) || "",
            // Critérios b–l
            pessoaNegraFamiliaCadunico: data.pessoa_negra_familia_cadunico ? 'yes' : 'no',
            pessoaComDeficienciaFamilia: data.pessoa_com_deficiencia_familia ? 'yes' : 'no',
            idosoNaFamilia: data.idoso_na_familia ? 'yes' : 'no',
            criancaOuAdolescenteFamilia: data.crianca_ou_adolescente_familia ? 'yes' : 'no',
            cancerOuDoencaRaraDegenerativa: data.cancer_ou_doenca_rara_degenerativa ? 'yes' : 'no',
            mulherVitimaViolenciaDomestica: data.mulher_vitima_violencia_domestica ? 'yes' : 'no',
            indigenaQuilombola: (data.indigena_quilombola as FormData['indigenaQuilombola']) || "",
            residenteAreaRisco: data.residente_area_risco ? 'yes' : 'no',
            tipoRisco: (data.tipo_risco as FormData['tipoRisco']) || "",
            contratoDistratadoOuRescindido: data.contrato_distratado_ou_rescindido ? 'yes' : 'no',
            coabitacaoRelatorioSocialSMASC: data.coabitacao_relatorio_social_smasc ? 'yes' : 'no',
            situacaoOcupacional: (data.situacao_ocupacional as FormData['situacaoOcupacional']) || "",
            declaration: !!data.declaracao_aceite,
          });
        }
      } catch (err) {
        // silencioso
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disableLoadOwnRegistration, editId]);

  const onError = (errors: any) => {
    const messages = Object.entries(errors)
      .map(([field, err]: any) => `${field}: ${err?.message || "Erro"}`)
      .join("\n");
    alert(`Erro de validação!\n\n${messages}`);
  };

  const onSubmit = async (data: FormData) => {
    try {
      const payload = {
        nome_completo: data.fullName,
        cpf: data.cpf.replace(/\D/g, ""),
        rg: data.rg || null,
        data_nascimento: data.birthDate || null,
        sexo: data.sexo || null,
        estado_civil: data.maritalStatus || null,
        telefone: data.phone.replace(/\D/g, "") || null,
        email: data.email || null,
        is_responsavel: data.isResponsible === "yes",
        nome_responsavel: data.responsibleName || null,
        endereco: data.address || null,
        cidade: data.city || null,
        estado: data.state || null,
        cep: data.cep.replace(/\D/g, "") || null,
        tamanho_familia: data.familySize ? Number(data.familySize) : 0,
        possui_filhos: data.hasChildren === "yes",
        possui_idosos: data.hasElderly === "yes",
        mae_solo: data.isSingleMother === "yes",
        possui_pcd: data.hasPCD === "yes",
        renda_individual: parseBRLCurrency(data.incomeIndividual),
        renda_familiar: parseBRLCurrency(data.incomeFamily),
        tempo_residencia_cidade: data.residenceYears || null,
        cadunico: data.cadunico || null,
        possui_imovel: data.hasImmovable === "yes",
        participou_programa_moradia: data.previousHousingProgram === "yes",
        idoso: data.selfIsElderly === "yes",
        pcd: data.selfIsPCD === "yes",
        declaracao_aceite: !!data.declaration,
        // Elegibilidade I–VI
        habitacao_precaria: data.habitacaoPrecaria === 'yes',
        parede_nao_alvenaria_ou_madeira_aparelhada: data.paredeNaoAlvenariaOuMadeira === 'yes',
        domicilio_particular_improvisado: data.domicilioImprovisado === 'yes',
        coabitacao: data.coabitacao === 'yes',
        autodeclaracao_coabitacao: data.autodeclaracaoCoabitacao || null,
        domicilio_alugado: data.domicilioAlugado === 'yes',
        total_residentes:
          data.totalResidentes !== undefined && data.totalResidentes !== ''
            ? Number(data.totalResidentes)
            : null,
        num_dormitorios: data.numDormitorios ? Number(data.numDormitorios) : null,
        aluguel_valor_mensal: data.aluguelValorMensal ? parseBRLCurrency(data.aluguelValorMensal) : null,
        aluguel_social_provisorio: data.aluguelSocialProvisorio === 'yes',
        situacao_rua: data.situacaoRua || null,
        // Critérios b–l
        pessoa_negra_familia_cadunico: data.pessoaNegraFamiliaCadunico === 'yes',
        pessoa_com_deficiencia_familia: data.pessoaComDeficienciaFamilia === 'yes',
        idoso_na_familia: data.idosoNaFamilia === 'yes',
        crianca_ou_adolescente_familia: data.criancaOuAdolescenteFamilia === 'yes',
        cancer_ou_doenca_rara_degenerativa: data.cancerOuDoencaRaraDegenerativa === 'yes',
        mulher_vitima_violencia_domestica: data.mulherVitimaViolenciaDomestica === 'yes',
        indigena_quilombola: data.indigenaQuilombola || null,
        residente_area_risco: data.residenteAreaRisco === 'yes',
        tipo_risco: data.tipoRisco || null,
        contrato_distratado_ou_rescindido: data.contratoDistratadoOuRescindido === 'yes',
        coabitacao_relatorio_social_smasc: data.coabitacaoRelatorioSocialSMASC === 'yes',
        situacao_ocupacional: data.situacaoOcupacional || null,
      };

      const response = editId
        ? await api.put(`/mcmv/pre-registrations/${editId}`, payload)
        : await api.post("/mcmv/pre-registrations", payload);
      const created = response.data?.data || response.data;

      // Mensagem de sucesso inline (sem alert)
      setSubmitMessage({ type: "success", text: "Inscrição enviada com sucesso!" });

      if (created && (created.numero_inscricao || created.data?.numero_inscricao)) {
        const numero_inscricao = created.numero_inscricao || created.data?.numero_inscricao;
        openRegistrationReceipt({
          numero_inscricao,
          nome: created.nome_completo || data.fullName,
          cpf: created.cpf || data.cpf,
          email: created.email || data.email,
          created_at: created.created_at,
          status: created.status || "inscricao_recebida",
        });
      }

      setRegistrationData(null);
      if (onSuccess) onSuccess();
    } catch (error: any) {
      console.error("Erro ao enviar inscrição:", error);
      const details = formatBackendErrors(error);
      const text = details
        ? `Falha de validação:\n${details}`
        : "Erro ao enviar a inscrição. Tente novamente.";
      setSubmitMessage({ type: "error", text });
    }
  };

  const StepWrapper = ({ children }: { children: any }) => (
    <section className="form-step">{children}</section>
  );

  const progressPercent = Math.min((currentStep / 6) * 100, 100);

  return (
    <div className="mcmv-form-container">
      <div className="form-header">
        <h1>{editId ? 'Editar Inscrição' : 'Inscrição MCMV'}</h1>
        <p>{editId ? 'Atualize os dados do inscrito.' : 'Preencha sua ficha de inscrição no programa.'}</p>
      </div>

      {submitMessage && (
        <div
          className={`message-banner ${submitMessage.type === "success" ? "message-success" : "message-error"}`}
          role="alert"
        >
          <span>{submitMessage.text}</span>
          <button type="button" className="message-close" onClick={() => setSubmitMessage(null)}>
            ×
          </button>
        </div>
      )}

      <div className="progress-bar">
        <div className="progress" style={{ width: `${progressPercent}%` }} />
      </div>

      <form onSubmit={handleSubmit(onSubmit, onError)} className="mcmv-form">
        {/* STEP 1 - Dados pessoais */}
        {currentStep === 1 && (
          <StepWrapper>
            <h2>1. Dados Pessoais</h2>

            <label className="form-group">Nome Completo
              <input {...register("fullName")} />
              {errors.fullName && <span className="error-message">{errors.fullName.message as string}</span>}
            </label>

            <label className="form-group">CPF
              <Controller
                name="cpf"
                control={control}
                render={({ field }) => (
                  <IMaskInput
                    mask="000.000.000-00"
                    value={field.value || ""}
                    onAccept={(val: any) => field.onChange(val)}
                  />
                )}
              />
              {errors.cpf && <span className="error-message">{errors.cpf.message as string}</span>}
            </label>

            <label className="form-group">RG
              <input {...register("rg")} />
              {errors.rg && <span className="error-message">{errors.rg.message as string}</span>}
            </label>

            <label className="form-group">Data de Nascimento
              <input type="date" {...register("birthDate")} />
              {errors.birthDate && <span className="error-message">{errors.birthDate.message as string}</span>}
            </label>

            <label className="form-group">Sexo
              <select {...register('sexo')}>
                <option value="">Selecione</option>
                <option value="M">Masculino</option>
                <option value="F">Feminino</option>
              </select>
              {errors.sexo && <span className="error-message">{errors.sexo.message as string}</span>}
            </label>

            <label className="form-group">Estado Civil
              <select {...register("maritalStatus")}>
                <option value="">Selecione</option>
                <option value="solteiro">Solteiro(a)</option>
                <option value="casado">Casado(a)</option>
                <option value="uniao_estavel">União Estável</option>
                <option value="divorciado">Divorciado(a)</option>
                <option value="viuvo">Viúvo(a)</option>
              </select>
              {errors.maritalStatus && <span className="error-message">{errors.maritalStatus.message as string}</span>}
            </label>

            <label className="form-group">Telefone
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <IMaskInput
                    mask="(00) 00000-0000"
                    value={field.value || ""}
                    onAccept={(val: any) => field.onChange(val)}
                  />
                )}
              />
              {errors.phone && <span className="error-message">{errors.phone.message as string}</span>}
            </label>

            <label className="form-group">E-mail
              <input type="email" {...register("email")} />
              {errors.email && <span className="error-message">{errors.email.message as string}</span>}
            </label>

            <label className="form-group">Você é o responsável pelo grupo familiar?
              <select {...register("isResponsible")}>
                <option value="yes">Sim</option>
                <option value="no">Não</option>
              </select>
            </label>

            {watch("isResponsible") === "no" && (
              <label className="form-group">Nome do responsável
                <input {...register("responsibleName")} />
              </label>
            )}

            <div className="form-navigation">
              <button type="button" className="btn btn-primary" onClick={nextStep}>Próximo</button>
            </div>
          </StepWrapper>
        )}

        {/* STEP 2 - Endereço */}
        {currentStep === 2 && (
          <StepWrapper>
            <h2>2. Endereço</h2>

            <div className="form-row">
              <label className="form-group">CEP
                <Controller
                  name="cep"
                  control={control}
                  render={({ field }) => (
                    <IMaskInput
                      mask="00000-000"
                      value={field.value || ""}
                      onAccept={(val: any) => {
                        // Apenas atualiza o campo; não faz auto-preenchimento pelo CEP
                        field.onChange(val);
                      }}
                    />
                  )}
                />
                {errors.cep && <span className="error-message">{errors.cep.message as string}</span>}
              </label>
              <label className="form-group flex-2">Endereço Completo
                <input {...register("address")} />
                {errors.address && <span className="error-message">{errors.address.message as string}</span>}
              </label>
              <label className="form-group">Cidade
                <input {...register("city")} />
                {errors.city && <span className="error-message">{errors.city.message as string}</span>}
              </label>
              <label className="form-group">Estado
                <select {...register("state")}>
                  <option value="">Selecione</option>
                  <option value="AC">Acre</option>
                  <option value="AL">Alagoas</option>
                  <option value="AP">Amapá</option>
                  <option value="AM">Amazonas</option>
                  <option value="BA">Bahia</option>
                  <option value="CE">Ceará</option>
                  <option value="DF">Distrito Federal</option>
                  <option value="ES">Espírito Santo</option>
                  <option value="GO">Goiás</option>
                  <option value="MA">Maranhão</option>
                  <option value="MT">Mato Grosso</option>
                  <option value="MS">Mato Grosso do Sul</option>
                  <option value="MG">Minas Gerais</option>
                  <option value="PA">Pará</option>
                  <option value="PB">Paraíba</option>
                  <option value="PR">Paraná</option>
                  <option value="PE">Pernambuco</option>
                  <option value="PI">Piauí</option>
                  <option value="RJ">Rio de Janeiro</option>
                  <option value="RN">Rio Grande do Norte</option>
                  <option value="RS">Rio Grande do Sul</option>
                  <option value="RO">Rondônia</option>
                  <option value="RR">Roraima</option>
                  <option value="SC">Santa Catarina</option>
                  <option value="SP">São Paulo</option>
                  <option value="SE">Sergipe</option>
                  <option value="TO">Tocantins</option>
                </select>
                {errors.state && <span className="error-message">{errors.state.message as string}</span>}
              </label>
            </div>

            <div className="form-navigation">
              <button type="button" className="btn btn-secondary" onClick={prevStep}>Voltar</button>
              <button type="button" className="btn btn-primary" onClick={nextStep}>Próximo</button>
            </div>
          </StepWrapper>
        )}

        {/* STEP 3 - Composição Familiar */}
        {currentStep === 3 && (
          <StepWrapper>
            <h2>3. Composição Familiar</h2>

            <label className="form-group">Número de Pessoas na Família
              <input {...register("familySize")} />
              {errors.familySize && <span className="error-message">{errors.familySize.message as string}</span>}
            </label>

            <div className="form-row">
              <label className="form-group">Crianças na família?
                <select {...register("hasChildren")}>
                  <option value="no">Não</option>
                  <option value="yes">Sim</option>
                </select>
              </label>

              <label className="form-group">Idosos na família?
                <select {...register("hasElderly")}>
                  <option value="no">Não</option>
                  <option value="yes">Sim</option>
                </select>
              </label>

              <label className="form-group">Mãe Solo (Mulher Chefe de Família)?
                <select {...register("isSingleMother")}>
                  <option value="no">Não</option>
                  <option value="yes">Sim</option>
                </select>
              </label>

              <label className="form-group">Algum membro possui deficiência (PCD)?
                <select {...register("hasPCD")}>
                  <option value="no">Não</option>
                  <option value="yes">Sim</option>
                </select>
              </label>
            </div>

            <div className="form-navigation">
              <button type="button" className="btn btn-secondary" onClick={prevStep}>Voltar</button>
              <button type="button" className="btn btn-primary" onClick={nextStep}>Próximo</button>
            </div>
          </StepWrapper>
        )}

        {/* STEP 4 - Renda */}
        {currentStep === 4 && (
          <StepWrapper>
            <h2>4. Renda</h2>

            <div className="form-row">
              <label className="form-group">Renda Individual (R$)
                <Controller
                  name="incomeIndividual"
                  control={control}
                  render={({ field }) => (
                    <IMaskInput
                      mask={Number}
                      scale={2}
                      radix="," 
                      mapToRadix={["."]}
                      thousandsSeparator="."
                      padFractionalZeros
                      value={field.value || ""}
                      onAccept={(val: any) => field.onChange(val)}
                    />
                  )}
                />
                {errors.incomeIndividual && <span className="error-message">{errors.incomeIndividual.message as string}</span>}
              </label>

              <label className="form-group">Renda Familiar Total (R$)
                <Controller
                  name="incomeFamily"
                  control={control}
                  render={({ field }) => (
                    <IMaskInput
                      mask={Number}
                      scale={2}
                      radix="," 
                      mapToRadix={["."]}
                      thousandsSeparator="."
                      padFractionalZeros
                      value={field.value || ""}
                      onAccept={(val: any) => field.onChange(val)}
                    />
                  )}
                />
                {errors.incomeFamily && <span className="error-message">{errors.incomeFamily.message as string}</span>}
              </label>
            </div>

            <div className="form-navigation">
              <button type="button" className="btn btn-secondary" onClick={prevStep}>Voltar</button>
              <button type="button" className="btn btn-primary" onClick={nextStep}>Próximo</button>
            </div>
          </StepWrapper>
        )}

        {/* STEP 5 - Critérios */}
        {currentStep === 5 && (
          <StepWrapper>
            <h2>5. Critérios do Programa</h2>

            <label className="form-group">Tempo de residência no município (anos)
              <input {...register("residenceYears")} />
              {errors.residenceYears && <span className="error-message">{errors.residenceYears.message as string}</span>}
            </label>

            <label className="form-group">Número do CadÚnico (NIS)
              <input {...register("cadunico")} />
              {errors.cadunico && <span className="error-message">{errors.cadunico.message as string}</span>}
            </label>

            <label className="form-group">O inscrito é idoso?
              <select {...register("selfIsElderly")}>
                <option value="no">Não</option>
                <option value="yes">Sim</option>
              </select>
            </label>

            <label className="form-group">O inscrito possui deficiência (PCD)?
              <select {...register("selfIsPCD")}>
                <option value="no">Não</option>
                <option value="yes">Sim</option>
              </select>
            </label>

            <label className="form-group">Possui imóvel urbano ou rural?
              <select {...register("hasImmovable")}>
                <option value="no">Não</option>
                <option value="yes">Sim</option>
              </select>
            </label>

            <label className="form-group">Já participou de programas habitacionais?
              <select {...register("previousHousingProgram")}>
                <option value="no">Não</option>
                <option value="yes">Sim</option>
              </select>
            </label>

            <h3 style={{ marginTop: 16 }}>Critérios de Elegibilidade (Déficit Habitacional)</h3>
            <div className="form-row">
              <label className="form-group">Habitação precária
                <select {...register('habitacaoPrecaria')}>
                  <option value="no">Não</option>
                  <option value="yes">Sim</option>
                </select>
              </label>
              <label className="form-group">Paredes não construídas em alvenaria ou madeira aparelhada
                <select {...register('paredeNaoAlvenariaOuMadeira')}>
                  <option value="no">Não</option>
                  <option value="yes">Sim</option>
                </select>
              </label>
              <label className="form-group">Domicílio improvisado
                <select {...register('domicilioImprovisado')}>
                  <option value="no">Não</option>
                  <option value="yes">Sim</option>
                </select>
              </label>
            </div>

            <div className="form-row">
              <label className="form-group">Coabitação
                <small style={{ display: 'block', color: '#666' }}>Vive com outra família no mesmo domicílio.</small>
                <select {...register('coabitacao')}>
                  <option value="no">Não</option>
                  <option value="yes">Sim</option>
                </select>
              </label>
              <label className="form-group flex-2">Autodeclaração de coabitação
                <small style={{ display: 'block', color: '#666' }}>Descreva com quem você coabita, há quanto tempo e o motivo.</small>
                <input {...register('autodeclaracaoCoabitacao')} />
              </label>
            </div>

            <div className="form-row">
              <label className="form-group">Domicílio alugado
                <select {...register('domicilioAlugado')}>
                  <option value="no">Não</option>
                  <option value="yes">Sim</option>
                </select>
              </label>
              <label className="form-group">Total de residentes
                <input type="number" min={0} {...register('totalResidentes')} />
              </label>
              <label className="form-group">Número de dormitórios
                <input type="number" min={0} {...register('numDormitorios')} />
              </label>
            </div>

            <div className="form-row">
              <label className="form-group">Valor do aluguel (R$)
                <input {...register('aluguelValorMensal')} />
              </label>
              <label className="form-group">Aluguel social provisório
                <select {...register('aluguelSocialProvisorio')}>
                  <option value="no">Não</option>
                  <option value="yes">Sim</option>
                </select>
              </label>
              <label className="form-group">Situação de rua
                <select {...register('situacaoRua')}>
                  <option value="">Selecione</option>
                  <option value="nao">Não</option>
                  <option value="rua">Rua</option>
                  <option value="trajetoria_rua">Trajetória de rua</option>
                </select>
              </label>
            </div>

            <h3 style={{ marginTop: 16 }}>Critérios Adicionais</h3>
            <div className="form-row">
              <label className="form-group">Pessoa negra (CadÚnico)
                <select {...register('pessoaNegraFamiliaCadunico')}>
                  <option value="no">Não</option>
                  <option value="yes">Sim</option>
                </select>
              </label>
              <label className="form-group">Pessoa com deficiência (família)
                <select {...register('pessoaComDeficienciaFamilia')}>
                  <option value="no">Não</option>
                  <option value="yes">Sim</option>
                </select>
              </label>
              <label className="form-group">Idoso na família
                <select {...register('idosoNaFamilia')}>
                  <option value="no">Não</option>
                  <option value="yes">Sim</option>
                </select>
              </label>
            </div>

            <div className="form-row">
              <label className="form-group">Criança ou adolescente na família
                <select {...register('criancaOuAdolescenteFamilia')}>
                  <option value="no">Não</option>
                  <option value="yes">Sim</option>
                </select>
              </label>
              <label className="form-group">Câncer/Doença rara degenerativa
                <select {...register('cancerOuDoencaRaraDegenerativa')}>
                  <option value="no">Não</option>
                  <option value="yes">Sim</option>
                </select>
              </label>
              <label className="form-group">Mulher vítima de violência doméstica
                <select {...register('mulherVitimaViolenciaDomestica')}>
                  <option value="no">Não</option>
                  <option value="yes">Sim</option>
                </select>
              </label>
            </div>

            <div className="form-row">
              <label className="form-group">Indígena/Quilombola
                <select {...register('indigenaQuilombola')}>
                  <option value="">Selecione</option>
                  <option value="nao">Não</option>
                  <option value="indigena">Indígena</option>
                  <option value="quilombola">Quilombola</option>
                  <option value="ambos">Ambos</option>
                </select>
              </label>
              <label className="form-group">Residente em área de risco
                <select {...register('residenteAreaRisco')}>
                  <option value="no">Não</option>
                  <option value="yes">Sim</option>
                </select>
              </label>
              <label className="form-group">Tipo de risco
                <select {...register('tipoRisco')}>
                  <option value="">Selecione</option>
                  <option value="nao">Não</option>
                  <option value="deslizamento">Deslizamento</option>
                  <option value="inundacao_brusca">Inundação brusca</option>
                  <option value="processo_geologico_hidrologico">Processo geológico/hidrológico</option>
                  <option value="outros">Outros</option>
                </select>
              </label>
            </div>

            <div className="form-row">
              <label className="form-group">Contrato distratado/rescindido
                <small style={{ display: 'block', color: '#666' }}>Teve contrato de moradia cancelado (distratado) ou rescindido anteriormente.</small>
                <select {...register('contratoDistratadoOuRescindido')}>
                  <option value="no">Não</option>
                  <option value="yes">Sim</option>
                </select>
              </label>
              <label className="form-group">Coabitação com relatório social SMASC
                <select {...register('coabitacaoRelatorioSocialSMASC')}>
                  <option value="no">Não</option>
                  <option value="yes">Sim</option>
                </select>
              </label>
              <label className="form-group">Situação ocupacional
                <select {...register('situacaoOcupacional')}>
                  <option value="">Selecione</option>
                  <option value="desemprego">Desemprego</option>
                  <option value="subemprego">Subemprego</option>
                  <option value="mercado_informal">Mercado informal</option>
                  <option value="emprego_formal">Emprego formal</option>
                  <option value="autonomo">Autônomo</option>
                  <option value="outros">Outros</option>
                </select>
              </label>
            </div>

            <div className="form-navigation">
              <button type="button" className="btn btn-secondary" onClick={prevStep}>Voltar</button>
              <button type="button" className="btn btn-primary" onClick={nextStep}>Próximo</button>
            </div>
          </StepWrapper>
        )}

        {/* STEP 6 - Finalização */}
        {currentStep === 6 && (
          <StepWrapper>
            <h2>6. Declaração e Envio</h2>

            <div className="checkbox-group">
              <input type="checkbox" {...register("declaration")} />
              <label>Declaro que todas as informações prestadas são verdadeiras.</label>
            </div>
            {errors.declaration && <span className="error-message">{errors.declaration.message as string}</span>}

            <div className="form-navigation">
              <button type="button" className="btn btn-secondary" onClick={prevStep}>Voltar</button>
              <button type="submit" className="btn btn-primary">{editId ? 'Salvar alterações' : 'Enviar Inscrição'}</button>
            </div>
          </StepWrapper>
        )}
      </form>
    </div>
  );
}

